"use client";

import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import HeroSearchBar from "./HeroSearchBar";
import { useReviewsAggregate } from "./useReviewsAggregate";

export interface SectionHeroVideoProps {
  className?: string;
}

// LCP optimisation. The hero used to lean on the <video poster> attribute,
// which browsers don't discover early enough to win an LCP race; we now
// render a real <img> as the LCP candidate (fetchPriority="high",
// loading="eager") and only swap to the looping video once it has buffered
// enough to play. The poster URL is downsized + lower-Q on purpose — it
// still looks identical at hero scale but cuts ~2 MB off the LCP load.
const HERO_POSTER =
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1280&q=55&auto=format&fm=webp";
// Hosted locally so the browser can use the same origin (no CSP
// `media-src` exemption needed, no third-party DNS hit) and Next.js's
// static-asset caching kicks in. Source: Mixkit license-free clip
// `4434-720`, downloaded once into /public/videos.
const HERO_VIDEO = "/videos/hero-mixkit-4434-720.mp4";

/**
 * Desktop hero — full-bleed looping video, glassmorphism overlay,
 * search bar + trust row. Mirrors the 2026 redesign artboard.
 *
 * Headline copy is intentionally hardcoded: the previous CMS-driven hero
 * lived on a different layout. Once the redesign is validated we can wire
 * the headline back to `cms-homepage-c138-4420-8330-79ef29e19177`.
 */
const SectionHeroVideo: FC<SectionHeroVideoProps> = ({ className = "" }) => {
  const { t } = useTranslation("guydeez");
  // Real site-wide rating + count (same source as the explore section and
  // the testimonials header). The chip simply doesn't render until the
  // aggregate lands — never shows fake numbers.
  const reviewsAggregate = useReviewsAggregate();

  // Defer the video tag until after first paint so the poster <img> wins
  // the LCP race. The video still autoplays the second it's mounted — the
  // user perceives the same hero, just ~2s sooner.
  const [videoMounted, setVideoMounted] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handle = window.requestIdleCallback
      ? window.requestIdleCallback(() => setVideoMounted(true), { timeout: 1200 })
      : window.setTimeout(() => setVideoMounted(true), 800);
    return () => {
      if (typeof handle === "number") window.clearTimeout(handle);
      else if (window.cancelIdleCallback) window.cancelIdleCallback(handle as any);
    };
  }, []);

  // When autoplay is BLOCKED (notably iOS Low Power Mode, which disables
  // video autoplay even for muted/playsInline clips), Safari paints a native
  // "tap to play" button over the <video>. We don't want that — the hero is
  // decorative. Try to play once mounted; if it's refused, unmount the video
  // so the poster <img> below covers the hero cleanly (no play button).
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoBlocked, setVideoBlocked] = useState(false);
  useEffect(() => {
    if (!videoMounted) return;
    const v = videoRef.current;
    if (!v) return;
    const attempt = v.play();
    if (attempt && typeof attempt.then === "function") {
      attempt.catch(() => setVideoBlocked(true));
    }
  }, [videoMounted]);

  return (
    <section
      className={`gdx relative w-full text-white ${className}`}
      style={{ height: 720, zIndex: 30 }}
    >
      {/* The preload + preconnect for this poster live in the server-rendered
          page.tsx — that gets them into the document <head> on the very
          first byte, so the browser can start the fetch in parallel with
          parsing instead of waiting for hydration. */}

      {/* Video + tint stack live inside their own clipped wrapper so the
          hero section itself can stay `overflow: visible` — that lets the
          AutoComplete dropdown spill past the hero's bottom edge instead of
          being clipped to it. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {/* LCP image. Stays mounted under the video so the poster keeps
            covering the area if/when the video buffers or fails. Explicit
            width/height attributes are present so the browser reserves the
            aspect-ratio box before the resource resolves — keeps the hero
            slot from CLS-shifting once the bitmap lands. */}
        <img
          src={HERO_POSTER}
          alt=""
          width={1280}
          height={720}
          aria-hidden="true"
          loading="eager"
          decoding="async"
          // @ts-expect-error — fetchPriority is a valid HTML attribute Lighthouse asks for.
          fetchpriority="high"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />
        {videoMounted && !videoBlocked && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0,
            }}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        )}

        {/* tint / vignette stack */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background:
              "linear-gradient(180deg, rgba(20,75,55,.55) 0%, rgba(10,45,32,.7) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background:
              "radial-gradient(60% 80% at 30% 50%, rgba(79,191,110,.18) 0%, transparent 70%)",
            mixBlendMode: "screen",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background:
              "radial-gradient(40% 60% at 80% 80%, rgba(212,123,107,.18) 0%, transparent 70%)",
            mixBlendMode: "screen",
          }}
        />
        {/* bottom fade so the section blends into the next white block */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 140,
            zIndex: 1,
            background: "linear-gradient(180deg, transparent, white 95%)",
          }}
        />
      </div>

      {/* content */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          padding: "80px 56px 0",
          maxWidth: 1100,
          margin: "0 auto",
          textAlign: "center",
          color: "white",
        }}
      >
        <h1
          style={{
            fontSize: 72,
            lineHeight: 1.0,
            fontWeight: 800,
            marginBottom: 18,
            letterSpacing: "-0.02em",
            textShadow: "0 4px 30px rgba(0,0,0,.35)",
            fontFamily: "var(--g-font-display)",
          }}
        >
          {t("homepage_hero_title_line1", { defaultValue: "Un guide local," })}
          <br />
          <span
            style={{
              color: "#F8E0E5",
              fontStyle: "italic",
              fontFamily: "var(--g-font-display)",
            }}
          >
            {t("homepage_hero_title_line2", { defaultValue: "une ville qui vous appartient." })}
          </span>
        </h1>

        <p
          style={{
            fontSize: 18,
            opacity: 0.92,
            maxWidth: 620,
            margin: "0 auto 22px",
            lineHeight: 1.5,
            textShadow: "0 2px 12px rgba(0,0,0,.3)",
          }}
        >
          {t("homepage_hero_subtitle", {
            defaultValue:
              "Réservez en 90 secondes une visite privée et 100% sur-mesure avec un Guydeez — guide local trié sur le volet.",
          })}
        </p>

        {/* Search bar — own component so its design (eyebrow + pin + CTA)
            stays isolated from the surrounding hero markup. Clicking it
            opens the shared SearchModal (same as the topbar search icon). */}
        <HeroSearchBar />

        {/* Trust row.
            The rating chip uses the live aggregate from `/api/reviews-
            aggregate` (same source as the explore section and the
            testimonials). Chip is only rendered once the aggregate lands —
            no hardcoded placeholder numbers. */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "stretch",
            gap: 0,
            padding: 6,
            background: "rgba(255,255,255,.14)",
            backdropFilter: "blur(12px)",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,.22)",
            boxShadow: "0 12px 40px rgba(0,0,0,.25)",
          }}
        >
          {reviewsAggregate && reviewsAggregate.totalReviews > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 20px",
                background: "rgba(255,255,255,.95)",
                borderRadius: 999,
                color: "var(--g-burgundy)",
              }}
              aria-label={`${reviewsAggregate.avgRating} sur 5, ${reviewsAggregate.totalReviews} avis`}
            >
              <StarsRow score={reviewsAggregate.avgRating} size={16} />
              <span style={{ fontWeight: 800, fontSize: 16 }}>
                {reviewsAggregate.avgRating.toLocaleString("fr-FR", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
                /5
              </span>
              <span
                style={{ fontSize: 13, color: "var(--g-ink-500)", fontWeight: 500 }}
              >
                · {reviewsAggregate.totalReviews.toLocaleString("fr-FR")}{" "}
                {t("homepage_hero_reviews_word", { defaultValue: "avis" })}
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              color: "white",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <ShieldIcon />{" "}
            {t("homepage_hero_trust_cancel", { defaultValue: "Annulation gratuite 24h" })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              color: "white",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <LockIcon />{" "}
            {t("homepage_hero_trust_payment", { defaultValue: "Paiement sécurisé" })}
          </div>
        </div>
      </div>
    </section>
  );
};

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7EE6A0" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7EE6A0" strokeWidth="2">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </svg>
);

/**
 * 5-star row with partial fill for fractional scores (4.4 → 4 full + one
 * star at 40% gold). Two stacked rows (grey behind, gold in front), the
 * front row clipped by width = score/5. No SVG `defs` / no React.useId so
 * the markup stays portable.
 */
const StarsRow: FC<{ score: number; size: number }> = ({ score, size }) => {
  const clamped = Math.max(0, Math.min(5, score));
  const pct = (clamped / 5) * 100;
  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
        lineHeight: 0,
        height: size,
      }}
      aria-hidden
    >
      <StarsLine color="#E8EFEA" size={size} />
      <span
        style={{
          position: "absolute",
          inset: 0,
          width: `${pct}%`,
          overflow: "hidden",
        }}
      >
        <StarsLine color="#F5B400" size={size} />
      </span>
    </span>
  );
};

const STAR_PATH =
  "M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z";

const StarsLine: FC<{ color: string; size: number }> = ({ color, size }) => (
  <span style={{ display: "inline-flex", gap: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d={STAR_PATH} />
      </svg>
    ))}
  </span>
);

export default SectionHeroVideo;
